import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db, auth } from './firebase'; 
import { collection, onSnapshot, query, orderBy, deleteDoc, getDocs, doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import * as Icons from 'react-icons/fa'; 
import { 
  MdDoneAll, MdMoreVert, MdSearch, MdAttachFile, MdInsertEmoticon, 
  MdSend, MdMic, MdCall, MdVideoCall, MdArrowBack, MdImage, MdCameraAlt, MdMessage, MdFiberManualRecord 
} from 'react-icons/md'; 

// Sound for new messages
const NOTIFICATION_SOUND = "https://assets.mixkit.co/sfx/preview/mixkit-software-interface-start-2574.mp3";

function Chat() {
  const [user, setUser] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); 
  
  // 🔥 API Credentials State
  const [apiCreds, setApiCreds] = useState({ token: "", phoneId: "" });

  // UI States
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showDialer, setShowDialer] = useState(false);
  const [dialNumber, setDialNumber] = useState("");
  const [showChatMenu, setShowChatMenu] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  
  // Notification States
  const [unread, setUnread] = useState({}); 

  // Refs
  const isSendingRef = useRef(false);
  const selectedContactRef = useRef(null); 
  const isFirstLoadRef = useRef(true);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // --- WHATSAPP THEME ---
  const theme = {
    primary: "#008069",
    bg: "#e3e5e8",
    chatBg: "#efeae2",
    sidebarBg: "#ffffff",
    myBubble: "#d9fdd3",
    otherBubble: "#ffffff",
    inputBar: "#f0f2f5",
    textMain: "#111b21",
    textSub: "#667781",
    blueTick: "#53bdeb",
    greyTick: "#8696a0",
    unread: "#25D366",
    unreadHighlightBg: "#e6f4ea" // New color for unread chat backgrounds
  };

  const quickReplies = ["Hi! Availability?", "Send Menu 💆‍♀️", "Confirm Booking ✅", "Call me later 📞"];
  const emojis = ['😀', '😂', '😍', '🙏', '👍', '🔥', '🎉', '❤️', '🌿', '💆‍♀️', '✨', '📅', '💸', '✅', '😭', '👋'];

  // --- CSS Animations for New Messages ---
  const customStyles = `
    @keyframes pulse-badge {
      0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7); }
      70% { transform: scale(1.05); box-shadow: 0 0 0 6px rgba(37, 211, 102, 0); }
      100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(37, 211, 102, 0); }
    }
    .unread-chat-row { transition: all 0.3s ease; }
    .unread-chat-row:hover { filter: brightness(0.95); }
  `;

  // --- 1. SETUP ---
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    const unsubscribe = auth.onAuthStateChanged((u) => setUser(u));
    return () => {
      window.removeEventListener('resize', handleResize);
      unsubscribe();
    };
  }, []);

  // --- 2. 🔥 UNIVERSAL CREDENTIAL LOADER ---
  useEffect(() => {
    if (!user) return;
    const fetchCreds = async () => {
      try {
        let token = "";
        let phoneId = "";

        const settingsRef = doc(db, "businesses", user.uid, "settings", "whatsapp");
        const settingsSnap = await getDoc(settingsRef);
        
        if (settingsSnap.exists() && settingsSnap.data().accessToken) {
          token = settingsSnap.data().accessToken;
          phoneId = settingsSnap.data().phoneNumberId;
        } else {
          const mainRef = doc(db, "businesses", user.uid);
          const mainSnap = await getDoc(mainRef);
          if (mainSnap.exists() && mainSnap.data().accessToken) {
            token = mainSnap.data().accessToken;
            phoneId = mainSnap.data().phoneId; 
          }
        }

        if (token && phoneId) {
          setApiCreds({ token, phoneId });
        }
      } catch (error) { console.error("Error fetching API keys:", error); }
    };
    fetchCreds();
  }, [user]);

  useEffect(() => {
    selectedContactRef.current = selectedContact;
    if (selectedContact) {
      setUnread(prev => ({ ...prev, [selectedContact.id]: false }));
    }
  }, [selectedContact]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  }, [messages, selectedContact, showEmojis]);

  // --- 3. CONTACTS LISTENER ---
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "businesses", user.uid, "contacts"), orderBy("lastActive", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!isFirstLoadRef.current) {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'modified' || change.type === 'added') {
            const contactId = change.doc.id;
            if (selectedContactRef.current?.id !== contactId) {
              setUnread(prev => ({ ...prev, [contactId]: true }));
              new Audio(NOTIFICATION_SOUND).play().catch(() => {});
              
              if ("Notification" in window && Notification.permission === "granted") {
                const contactData = change.doc.data();
                new Notification(contactData.clientName || contactData.phone || "New Message", {
                  body: contactData.lastMessage || "You received a new message",
                  icon: "https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                });
              }
            }
          }
        });
      }
      isFirstLoadRef.current = false;
      setContacts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, [user]);

  // --- 4. CHAT MESSAGES LISTENER ---
  useEffect(() => {
    if (!user || !selectedContact) return;
    const q = query(collection(db, "businesses", user.uid, "chats", selectedContact.id, "messages"), orderBy("timestamp", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const rawMessages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const uniqueMessages = [];
      rawMessages.forEach((msg, index) => {
        if (index > 0) {
          const prevMsg = rawMessages[index - 1];
          const timeDiff = Math.abs((msg.timestamp?.toMillis() || 0) - (prevMsg.timestamp?.toMillis() || 0));
          if (msg.text === prevMsg.text && msg.sender === prevMsg.sender && timeDiff < 2000) return;
        }
        uniqueMessages.push(msg);
      });
      setMessages(uniqueMessages);
    });
    return () => unsubscribe();
  }, [user, selectedContact]);

  const filteredContacts = useMemo(() => {
    return contacts.filter(c => c.phone.includes(searchQuery) || (c.clientName && c.clientName.toLowerCase().includes(searchQuery.toLowerCase())));
  }, [contacts, searchQuery]);

  const getDisplayName = (contact) => {
    if (!contact) return "";
    return (contact.clientName && contact.clientName.trim() !== "") ? contact.clientName : contact.phone;
  };

  const renderAvatar = (contact, size = 49) => {
    const hasImage = contact.profilePic || contact.photoURL;
    if (hasImage) return <img src={hasImage} alt="dp" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />;
    const name = getDisplayName(contact) || "#";
    return (
      <div style={{ width: size, height: size, borderRadius: '50%', background: '#dfe4ea', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontWeight: 'bold', fontSize: size * 0.4 }}>
         {name.substring(0, 2).toUpperCase()}
      </div>
    );
  };

  const formatTime = (dateObj) => {
    if (!dateObj) return "";
    return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const getDateLabel = (date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return "TODAY";
    if (date.toDateString() === yesterday.toDateString()) return "YESTERDAY";
    return date.toLocaleDateString();
  };

  const handleDirectDial = () => {
    if(!dialNumber) return;
    const cleanNumber = dialNumber.replace(/\D/g, ''); 
    if(cleanNumber.length < 10) { alert("Invalid Number"); return; }
    
    const existing = contacts.find(c => c.phone === cleanNumber);
    if(existing) {
      setSelectedContact(existing);
    } else {
      setSelectedContact({ 
        id: cleanNumber, phone: cleanNumber, clientName: "", 
        lastMessage: "Drafting...", lastActive: new Date() 
      });
    }
    setShowDialer(false);
    setDialNumber("");
  };

  const handleCall = () => {
    if (!selectedContact || !selectedContact.phone) return alert("No phone number found.");
    let cleanPhone = selectedContact.phone.replace(/[^\d+]/g, '');
    if (cleanPhone.length === 12 && cleanPhone.startsWith('91')) { cleanPhone = '+' + cleanPhone; }
    window.location.href = `tel:${cleanPhone}`;
  };

  // --- 🔥 SEND FUNCTION ---
  const sendMessage = async (e, forcedText = null) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    if (isSendingRef.current) return;

    const textToSend = forcedText || inputText;
    if (!textToSend.trim() || !selectedContact || !user) return;

    if (!apiCreds.token || !apiCreds.phoneId) {
      alert("⚠️ Connection Error: No WhatsApp Token found.");
      return;
    }

    isSendingRef.current = true;
    setInputText("");
    setShowEmojis(false);
    setShowAttachMenu(false);

    try {
      const contactId = selectedContact.id; 
      const messageId = "msg_" + Date.now() + "_" + Math.floor(Math.random() * 1000);

      const contactRef = doc(db, "businesses", user.uid, "contacts", contactId);
      const messageRef = doc(db, "businesses", user.uid, "chats", contactId, "messages", messageId);

      await setDoc(contactRef, {
        phone: contactId,
        lastMessage: textToSend,
        lastActive: serverTimestamp(),
        ...(selectedContact.clientName ? { clientName: selectedContact.clientName } : {}) 
      }, { merge: true });

      await setDoc(messageRef, {
        text: textToSend, sender: 'bot', status: 'sent', timestamp: serverTimestamp()
      });

      const response = await fetch(`https://graph.facebook.com/v24.0/${apiCreds.phoneId}/messages`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${apiCreds.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ messaging_product: 'whatsapp', to: contactId, type: 'text', text: { body: textToSend } })
      });
      
      const data = await response.json();
      if (data.error) {
        console.error("Meta Error:", data.error);
        alert(`Meta Error: ${data.error.message}`);
      }
    } catch (error) {
      alert("Failed to send: " + error.message);
    } finally {
      setTimeout(() => { isSendingRef.current = false; }, 500);
    }
  };

  const clearChat = async () => {
    if (!window.confirm("Clear chat?")) return;
    const q = query(collection(db, "businesses", user.uid, "chats", selectedContact.id, "messages"));
    const snapshot = await getDocs(q);
    snapshot.forEach(async (messageDoc) => await deleteDoc(messageDoc.ref));
    await setDoc(doc(db, "businesses", user.uid, "contacts", selectedContact.id), { lastMessage: "" }, { merge: true });
    setShowChatMenu(false);
  };

  if (!user) return <div style={{height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>Loading WhatsApp...</div>;

  return (
    <div style={{ display: 'flex', width: '100%', height: '100vh', fontFamily: '"Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif', backgroundColor: theme.bg, overflow: 'hidden', position: 'relative' }}>
      <style>{customStyles}</style>
      
      {/* --- SIDEBAR (CONTACTS) --- */}
      <div style={{ display: (isMobile && selectedContact) ? 'none' : 'flex', width: isMobile ? '100%' : '380px', flexDirection: 'column', background: theme.sidebarBg, borderRight: '1px solid #d1d7db', height: '100%', position: 'relative' }}>
        <div style={{ padding: '10px 16px', background: theme.primary, height: '60px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, color: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icons.FaUserCircle style={{ fontSize: '38px', color: 'white' }} /></div>
            {isMobile && <span style={{ fontSize: '20px', fontWeight: '500' }}>WhatsApp</span>}
          </div>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
             <MdMessage style={{ fontSize: '26px', cursor: 'pointer' }} onClick={() => setShowDialer(true)} />
             <MdMoreVert style={{ fontSize: '24px', cursor: 'pointer' }} />
          </div>
        </div>
        
        <div style={{ padding: '10px', borderBottom: '1px solid #f0f2f5' }}>
          <div style={{ background: '#f0f2f5', borderRadius: '8px', padding: '8px 15px', display: 'flex', alignItems: 'center' }}>
            <MdSearch style={{ fontSize: '20px', color: '#54656f', marginRight: '15px' }} />
            <input type="text" placeholder="Search or start new chat" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ width: '100%', border: 'none', background: 'transparent', outline: 'none', fontSize: '14px' }} />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filteredContacts.map(contact => {
            const isUnread = unread[contact.id];
            const isSelected = selectedContact?.id === contact.id;
            
            return (
            <div 
              key={contact.id} 
              onClick={() => setSelectedContact(contact)} 
              className={isUnread ? "unread-chat-row" : ""}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: '0 15px', 
                height: '75px', 
                cursor: 'pointer', 
                // BOLD NOTIFICATION STYLES APPLED HERE:
                background: isSelected ? '#f0f2f5' : (isUnread ? theme.unreadHighlightBg : 'transparent'), 
                borderBottom: '1px solid #f0f2f5',
                borderLeft: isUnread ? `4px solid ${theme.unread}` : '4px solid transparent'
              }}
            >
              <div style={{ marginRight: '15px' }}>{renderAvatar(contact, 49)}</div>
              <div style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingRight: '5px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                  
                  {/* BOLD NAME FOR NEW MESSAGES */}
                  <span style={{ fontSize: isUnread ? '18px' : '17px', color: '#111b21', fontWeight: isUnread ? '800' : '400' }}>
                    {getDisplayName(contact)}
                  </span>
                  
                  {contact.lastActive && <span style={{ fontSize: '12px', color: isUnread ? theme.unread : '#667781', fontWeight: isUnread ? '700' : '400' }}>{formatTime(contact.lastActive.toDate())}</span>}
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: '14px', color: isUnread ? '#111b21' : '#667781', fontWeight: isUnread ? '600' : '400', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>
                    {contact.lastMessage}
                  </div>
                  
                  {/* PULSING NEW BADGE */}
                  {isUnread && (
                    <div style={{ background: theme.unread, color: 'white', fontSize: '10px', fontWeight: '800', padding: '3px 8px', borderRadius: '12px', letterSpacing: '0.5px', animation: 'pulse-badge 2s infinite' }}>
                      NEW
                    </div>
                  )}
                </div>
              </div>
            </div>
          )})}
        </div>
        <div onClick={() => setShowDialer(true)} style={{ position: 'absolute', bottom: '20px', right: '20px', width: '55px', height: '55px', background: theme.primary, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.3)', cursor: 'pointer', zIndex: 100 }}><MdMessage style={{ color: 'white', fontSize: '24px' }} /></div>
      </div>
      
      {/* --- MAIN CHAT WINDOW --- */}
      <div style={{ display: (!isMobile || selectedContact) ? 'flex' : 'none', flex: 1, flexDirection: 'column', background: theme.chatBg, position: isMobile ? 'fixed' : 'relative', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, height: '100%' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0.4, backgroundImage: "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')", pointerEvents: 'none', zIndex: 0 }}></div>

        {selectedContact ? (
          <>
            <div style={{ height: '60px', background: theme.primary, padding: '0 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 10, flexShrink: 0, color: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <MdArrowBack onClick={() => setSelectedContact(null)} style={{ fontSize: '24px', marginRight: '5px' }} />
                <div style={{ marginRight: '10px' }}>{renderAvatar(selectedContact, 38)}</div>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '500' }}>{getDisplayName(selectedContact)}</div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>online</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '15px' }}>
                <MdVideoCall style={{ fontSize: '24px', cursor: 'pointer' }} />
                <MdCall style={{ fontSize: '22px', cursor: 'pointer' }} onClick={handleCall} />
                <MdMoreVert style={{ fontSize: '24px', cursor: 'pointer' }} onClick={() => setShowChatMenu(!showChatMenu)} />
                {showChatMenu && (
                  <div style={{ position: 'absolute', top: '50px', right: '10px', background: 'white', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 100, padding: '5px 0' }}>
                    <div onClick={clearChat} style={{ padding: '12px 20px', fontSize: '14.5px', color: '#111b21', cursor: 'pointer' }}>Clear chat</div>
                    <div onClick={() => setShowChatMenu(false)} style={{ padding: '12px 20px', fontSize: '14.5px', color: '#111b21', cursor: 'pointer' }}>Close</div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ flex: 1, padding: '15px 10px', overflowY: 'auto', display: 'flex', flexDirection: 'column', zIndex: 5 }}>
               <div style={{ alignSelf: 'center', background: '#ffeecd', color: '#54656f', fontSize: '12px', padding: '5px 12px', borderRadius: '8px', marginBottom: '15px', textAlign: 'center', boxShadow: '0 1px 1px rgba(0,0,0,0.1)', maxWidth: '90%' }}>
                 🔒 Messages are end-to-end encrypted.
               </div>
               {messages.map((msg, index) => {
                 const msgDate = msg.timestamp ? (typeof msg.timestamp.toDate === 'function' ? msg.timestamp.toDate() : new Date(msg.timestamp)) : new Date();
                 const prevMsgDate = index > 0 && messages[index - 1].timestamp ? (typeof messages[index - 1].timestamp.toDate === 'function' ? messages[index - 1].timestamp.toDate() : new Date(messages[index - 1].timestamp)) : null;
                 const showDate = !prevMsgDate || msgDate.toDateString() !== prevMsgDate.toDateString();
                 return (
                   <React.Fragment key={msg.id || index}>
                     {showDate && <div style={{ alignSelf: 'center', background: '#ffffff', color: '#54656f', padding: '5px 10px', borderRadius: '8px', fontSize: '12px', margin: '10px 0', boxShadow: '0 1px 1px rgba(0,0,0,0.1)', textTransform: 'uppercase', fontWeight: '500' }}>{getDateLabel(msgDate)}</div>}
                     <div style={{ alignSelf: msg.sender === 'bot' ? 'flex-end' : 'flex-start', background: msg.sender === 'bot' ? theme.myBubble : theme.otherBubble, padding: '6px 8px 8px 10px', borderRadius: '8px', marginBottom: '4px', maxWidth: '85%', boxShadow: '0 1px 0.5px rgba(11,20,26,0.13)', fontSize: '14.2px', lineHeight: '19px', position: 'relative' }}>
                       <span>{msg.text}</span>
                       <div style={{ float: 'right', fontSize: '11px', color: '#667781', marginTop: '6px', marginLeft: '8px', display: 'flex', alignItems: 'center', height: '15px' }}>
                         {formatTime(msgDate)}
                         {msg.sender === 'bot' && <span style={{ marginLeft: '3px', color: msg.status === 'read' ? theme.blueTick : theme.greyTick }}><MdDoneAll style={{ fontSize: '16px' }} /></span>}
                       </div>
                     </div>
                   </React.Fragment>
                 );
               })}
               <div ref={messagesEndRef} />
            </div>

            <div style={{ padding: '8px 10px', display: 'flex', gap: '8px', overflowX: 'auto', zIndex: 10, flexShrink: 0 }}>
              {quickReplies.map((reply, i) => (
                <button key={i} onClick={() => sendMessage(null, reply)} style={{ background: 'white', border: '1px solid #e9edef', borderRadius: '16px', padding: '6px 12px', fontSize: '13px', color: '#54656f', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', whiteSpace: 'nowrap', cursor: 'pointer' }}>{reply}</button>
              ))}
            </div>

            <div style={{ background: theme.inputBar, padding: '5px 8px', display: 'flex', alignItems: 'center', gap: '8px', minHeight: '60px', zIndex: 10, flexShrink: 0 }}>
              <MdInsertEmoticon style={{ fontSize: '26px', color: '#54656f', cursor: 'pointer' }} onClick={() => setShowEmojis(!showEmojis)} />
              <div style={{ position: 'relative' }}>
                <MdAttachFile style={{ fontSize: '26px', color: '#54656f', transform: 'rotate(45deg)', cursor: 'pointer' }} onClick={() => setShowAttachMenu(!showAttachMenu)} />
                {showAttachMenu && <div style={{ position: 'absolute', bottom: '60px', left: '0', display: 'flex', flexDirection: 'column', gap: '15px', zIndex: 200 }}>
                    <div style={{ width: 50, height: 50, borderRadius: '50%', background: '#bf59cf', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 5px rgba(0,0,0,0.3)', cursor: 'pointer' }} onClick={() => fileInputRef.current.click()}><MdImage color="white" size={24}/></div>
                    <div style={{ width: 50, height: 50, borderRadius: '50%', background: '#d3396d', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 5px rgba(0,0,0,0.3)', cursor: 'pointer' }}><MdCameraAlt color="white" size={24}/></div>
                  </div>}
              </div>
              <input type="file" ref={fileInputRef} hidden />
              <div style={{ flex: 1, background: '#ffffff', borderRadius: '20px', padding: '10px 15px', display: 'flex', alignItems: 'center' }}>
                <input value={inputText} onChange={(e) => setInputText(e.target.value)} placeholder="Message" onKeyDown={(e) => { if(e.key === 'Enter') sendMessage(e); }} style={{ width: '100%', border: 'none', outline: 'none', fontSize: '15px', background: 'transparent' }} />
              </div>
              <div onClick={(e) => sendMessage(e)} style={{ width: '45px', height: '45px', borderRadius: '50%', background: theme.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', cursor: 'pointer' }}>
                 {inputText.trim() ? <MdSend size={20} /> : <MdMic size={22} />}
              </div>
            </div>
            {showEmojis && <div style={{ padding: '10px', background: '#e9edef', display: 'flex', gap: '15px', overflowX: 'auto', zIndex: 10, flexShrink: 0 }}>{emojis.map(e => <span key={e} onClick={() => setInputText(prev => prev + e)} style={{ fontSize: '28px', cursor: 'pointer' }}>{e}</span>)}</div>}
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderBottom: '6px solid #25D366', background: '#f0f2f5' }}>
             <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" width="80" style={{ opacity: 0.3, marginBottom: '20px' }} />
             <h1 style={{ color: '#41525d', fontWeight: '300' }}>Tesla CRM Connect</h1>
             <p style={{ color: '#667781', fontSize: '14px', marginTop: '10px' }}>Select a chat to start messaging securely.</p>
          </div>
        )}
      </div>

      {showDialer && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(255,255,255,0.95)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', width: '300px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)', border: '1px solid #e9edef', textAlign: 'center' }}>
             <h3 style={{ margin: '0 0 10px 0', color: theme.textMain }}>Start New Chat</h3>
             <input autoFocus type="tel" placeholder="e.g. 919876543210" value={dialNumber} onChange={(e) => setDialNumber(e.target.value)} style={{ width: '85%', padding: '12px', border: '1px solid #e9edef', borderRadius: '8px', fontSize: '16px', marginBottom: '20px', textAlign: 'center', background: '#f0f2f5' }} />
             <div style={{ display: 'flex', gap: '10px' }}>
               <button onClick={() => setShowDialer(false)} style={{ flex: 1, padding: '12px', border: 'none', background: '#f0f2f5', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
               <button onClick={handleDirectDial} style={{ flex: 1, padding: '12px', border: 'none', background: theme.primary, color: 'white', borderRadius: '8px', cursor: 'pointer' }}>Chat</button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Chat;